import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import cron from "cron-validate";
import { useSnackbar } from "notistack";
import React, { useEffect } from "react";
import { SUGGESTED_TAGS } from "../../constants/tags";
import { useApiMutation } from "../../hooks";
import { useAvailableTags } from "../../hooks/use-tags";
import type { AiRequest, AiResponse, TestSuite, TestSuiteSchema } from "../../types";
import { getTagStyle } from "../../utils/tag-styles";

export interface TestSuiteDialogProps {
  open: boolean;
  mode: "create" | "edit";
  onClose: () => void;
  onSave: (data: TestSuiteSchema) => void;
  data: TestSuiteSchema | TestSuite;
  setData: (data: TestSuiteSchema | TestSuite | null) => void;
  isProcessing: boolean;
}

const TestSuiteDialog: React.FC<TestSuiteDialogProps> = ({
  open,
  mode,
  onClose,
  onSave,
  data,
  setData,
  isProcessing,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const title = mode === "create" ? "New Test Suite" : "Edit Test Suite";
  const buttonText = mode === "create" ? "Create" : "Save";
  const description =
    mode === "create"
      ? "Create a new test suite, so that you can run multiple tests at once."
      : "Edit your test suite properties.";

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setData({
      ...data,
      [name]: value,
    });
  };

  const handleScheduleEnabledChange = (event: SelectChangeEvent) => {
    setData({
      ...data,
      schedule_enabled: event.target.value === "on",
    });
  };

  const [aiText, setAiText] = React.useState<string | null>(null);

  const {
    mutate: ai,
    data: aiData,
    isPending: isAiProcessing,
    reset,
  } = useApiMutation<AiRequest, AiResponse>("POST", "/ai/response");

  // Tag selection functionality
  const { data: availableTagsData } = useAvailableTags();
  const availableTags = availableTagsData?.tags || [];

  // Combine available tags with suggested tags, removing duplicates
  const allSuggestedTags = [...new Set([...availableTags, ...SUGGESTED_TAGS])];

  useEffect(() => {
    if (aiData?.resp) {
      if (!cron(aiData.resp).isValid()) {
        enqueueSnackbar(aiData.resp, {
          variant: "error",
          anchorOrigin: {
            horizontal: "right",
            vertical: "bottom",
          },
        });
        return;
      }

      setAiText(null);

      setData({
        ...data,
        schedule: aiData.resp,
      });

      enqueueSnackbar("CRON expression generated successfully", {
        variant: "success",
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });

      reset();
    }
  }, [aiData, setData, data, enqueueSnackbar, reset]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText>{description}</DialogContentText>
        <Stack direction="column" spacing={2} alignItems="stretch" justifyContent="center" mt={1}>
          <TextField
            autoFocus
            required
            id="name"
            name="name"
            label="Name"
            placeholder="Daily acceptance tests"
            size="small"
            type="text"
            fullWidth
            value={data.name}
            onChange={handleChange}
          />
          <TextField
            required={false}
            id="description"
            name="description"
            label="Description"
            placeholder="E.g. daily acceptance tests after deploy to staging..."
            size="small"
            multiline
            minRows={2}
            maxRows={4}
            type="text"
            fullWidth
            value={data.description || ""}
            onChange={handleChange}
          />
          <Grid container spacing={{ xs: 2 }}>
            <Grid size={{ xs: 6, md: 8 }}>
              <TextField
                required={false}
                id="schedule"
                name="schedule"
                label="Schedule CRON"
                placeholder="E.g. 0 0 * * * for daily"
                size="small"
                type="text"
                fullWidth
                error={!!data.schedule && data.schedule !== "" && !cron(data.schedule).isValid()}
                helperText={
                  !!data.schedule && data.schedule !== "" && !cron(data.schedule).isValid()
                    ? "Invalid CRON schedule"
                    : "Leave empty to run manually"
                }
                value={data.schedule || ""}
                onChange={handleChange}
              />
            </Grid>
            <Grid size={{ xs: 6, md: 4 }}>
              <Select
                fullWidth
                size="small"
                value={data.schedule_enabled ? "on" : "off"}
                onChange={handleScheduleEnabledChange}
              >
                <MenuItem value="on">Enabled</MenuItem>
                <MenuItem value="off">Disabled</MenuItem>
              </Select>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                required={false}
                id="schedule"
                name="schedule"
                label="What is your recurrence?"
                placeholder="E.g. Every day at midnight"
                size="small"
                type="text"
                fullWidth
                helperText="Do not know how to write a CRON? Write the recurrence you want and press Enter"
                disabled={isAiProcessing}
                value={aiText}
                onChange={(e) => setAiText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    ai({
                      query: `Generate a valid CRON expression for the following recurrence "${aiText}". Do not ask any questions, just provide the CRON expression. Feel free to make big assumptions to generate the CRON expression, or just guess something that is close enough. Only respond with the CRON expression and nothing else.`,
                      output_model: {
                        type: "string",
                        name: "cron",
                        description: "Just the VALID CRON expression, no explanation",
                      },
                    });
                  }
                }}
              />
            </Grid>
          </Grid>

          {/* Tag Filters Section */}
          <Typography variant="subtitle2" color="text.primary" fontWeight={600} mt={3} mb={1}>
            Tag Filters
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Select tags to dynamically include tests that have these tags. This allows you to run
            tests based on their tags rather than explicitly selecting each test. Tests with any of
            the selected tags will be automatically included in this suite.
          </Typography>

          <Autocomplete
            multiple
            size="small"
            options={allSuggestedTags}
            value={data.tag_filters || []}
            onChange={(_, value) => {
              setData({
                ...data,
                tag_filters: value,
              });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select tags to filter tests..."
                size="small"
                helperText="Tests with any of these tags will be included in this suite"
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const style = getTagStyle(option);
                return (
                  <Chip
                    {...getTagProps({ index })}
                    key={option}
                    label={option}
                    size="small"
                    sx={{
                      fontSize: "0.75rem",
                      height: 22,
                      backgroundColor: style.color,
                      borderColor: style.borderColor,
                      color: style.textColor,
                      "& .MuiChip-label": {
                        px: 1,
                      },
                      "& .MuiChip-deleteIcon": {
                        color: style.textColor,
                        fontSize: "0.875rem",
                      },
                    }}
                  />
                );
              })
            }
            renderOption={(props, option) => {
              const style = getTagStyle(option);
              return (
                <Box
                  component="li"
                  {...props}
                  sx={{
                    fontSize: "0.875rem",
                    "&:hover": {
                      backgroundColor: style.color,
                    },
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    py: 0.5,
                  }}
                >
                  <Chip
                    label={option}
                    size="small"
                    variant="outlined"
                    sx={{
                      fontSize: "0.75rem",
                      height: 20,
                      mr: 1,
                      backgroundColor: style.color,
                      borderColor: style.borderColor,
                      color: style.textColor,
                    }}
                  />
                  {option}
                </Box>
              );
            }}
            freeSolo
            autoComplete
            includeInputInList
            filterSelectedOptions
            clearOnBlur
            handleHomeEndKeys
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          disabled={!data.name || isProcessing}
          loading={isProcessing}
          onClick={() => {
            if (data) {
              const payload: TestSuiteSchema = {
                name: data.name,
                description: data.description,
                schedule: data.schedule,
                schedule_enabled: data.schedule_enabled,
                test_ids: data.test_ids,
                webhook_ids: data.webhook_ids || [],
                tag_filters: data.tag_filters || [],
                test_dependency_graph: data.test_dependency_graph || {},
                test_dependency_graph_conditions: data.test_dependency_graph_conditions || {},
              };
              onSave(payload);
            }
          }}
        >
          {buttonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TestSuiteDialog;
